import type { Listing } from 'api/ListingController/getListing';
import type { RecipientType } from '../OfferMessageSentPage/types';
import type { ListingAgentType } from './types';

/**
 * Function checks and remove duplicate listing agents from Recipients section
 */
export const checkForDuplicatesLA = (
  agentsRecipient_: RecipientType[],
  listingAgentsRecipient_: ListingAgentType[],
) => {
  const listingAgentEmailSet = new Set(
    listingAgentsRecipient_.map(({ email }: ListingAgentType) => email),
  );
  const listingAgentIdSet = new Set(
    listingAgentsRecipient_.map(({ id }: ListingAgentType) => id),
  );

  // APP-5042 Duplicate Email allowed
  return agentsRecipient_.filter(({ email, id }: RecipientType) => {
    if (id) {
      return !listingAgentIdSet.has(id);
    }
    return !listingAgentEmailSet.has(email);
  });
};

/**
 * Function returns listingAgentRecipient data
 */
export const getListingAgents = (listing_: Pick<Listing, 'reps' | 'org'>) => {
  const listingAgentsRecipientData = listing_.reps.map(agent => ({
      id: agent.id,
      name: `${agent.firstName} ${agent.lastName}`,
      email: agent.email,
      org:
        listing_.org.addresses[+agent.location][+agent.address].name ||
        listing_.org.name,
    }));

  return listingAgentsRecipientData;
};

// checks to see if any recipient has been removed after fetching the recepient data in fetchAgents
export const checkIfRecipientRemoved = (
  agentsRecipient_: RecipientType[],
  listingAgentsRecipient_: ListingAgentType[],
  listingAgentsRemoved_: ListingAgentType[],
  agentsRemoved_: RecipientType[],
) => {
  let cleanedAgentsRecipient: RecipientType[] = agentsRecipient_;
  let newAgentsRemoved: RecipientType[] = [];

  let cleanedListingAgentsRecipient: ListingAgentType[] =
    listingAgentsRecipient_;
  let newListingAgentsRemoved: ListingAgentType[] = [];
  if (agentsRemoved_.length) {
    const agentsRecipientEmails = new Set([
      ...agentsRecipient_.map(({ email }) => email),
    ]);
    const agentsRecipientIds = new Set([
      ...agentsRecipient_.map(({ id }) => id).filter(Boolean),
    ]);

    newAgentsRemoved = agentsRemoved_.filter(({ id, email }) => {
      if (id) {
        agentsRecipientIds.has(id);
      }
      return agentsRecipientEmails.has(email);
    });

    const agentsRemovedEmails = new Set([
      ...newAgentsRemoved.map(({ email }) => email),
    ]);
    const agentsRemovedIds = new Set([
      ...newAgentsRemoved.map(({ id }) => id).filter(Boolean),
    ]);

    cleanedAgentsRecipient = agentsRecipient_.filter(({ id, email }) => {
      if (id) {
        return !agentsRemovedIds.has(id);
      }
      return !agentsRemovedEmails.has(email);
    });
  }
  if (listingAgentsRemoved_.length) {
    const listingAgentsRecipientEmails = new Set([
      ...listingAgentsRecipient_.map(({ email }) => email),
    ]);
    const listingAgentsRecipientIds = new Set([
      ...listingAgentsRecipient_.map(({ id }) => id),
    ]);

    newListingAgentsRemoved = listingAgentsRemoved_.filter(
      ({ id, email }: any) => {
        if (id) {
          return listingAgentsRecipientIds.has(email);
        }
        return listingAgentsRecipientEmails.has(email);
      },
    );

    const listingAgentsRemovedEmails = new Set([
      ...newListingAgentsRemoved.map(({ email }) => email),
    ]);
    const listingAgentsRemovedIds = new Set([
      ...newListingAgentsRemoved.map(({ id }) => id),
    ]);

    cleanedListingAgentsRecipient = listingAgentsRecipient_.filter(
      ({ id, email }) => {
        if (id) {
          return !listingAgentsRemovedIds.has(id);
        }
        return !listingAgentsRemovedEmails.has(email);
      },
    );
  }

  return {
    cleanedListingAgentsRecipient,
    cleanedAgentsRecipient,
    newAgentsRemoved,
    newListingAgentsRemoved,
  };
};
